import { Col, Row, Form, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormRadioGroup from '../../../../antd/form/FormRadioGroup/FormRadioGroup.jsx';
import FormTextEditor from '../../../../antd/form/FormTextEditor/FormTextEditor.jsx';
import FormTextField from '../../../../antd/form/FormTextField/FormTextField.jsx';
import FormSwitch from '../../../../antd/form/FormSwitch/FormSwitch.jsx';
import FormButton from '../../../../antd/form/FormButton/FormButton.jsx';
import { required } from '../../../../antd/form/FormValidation.js';
import {
  HEADING_PREFIX,
  TEXT_ALIGN,
  YELLOW_BOOKS_SECTION_TYPES,
} from '../../../../../utils/constants/enums.js';
import { CollapseBlock } from '../../../../common/index.jsx';
import FormSelect from '../../../../antd/form/FormSelect/FormSelect.jsx';
import { InfoCircleOutlined } from '@ant-design/icons';

const defaultValues = {
  item_type: YELLOW_BOOKS_SECTION_TYPES.TEXT.id,
  is_numbering: false,
  numbering_align: TEXT_ALIGN.LEFT.id,
  numbering_prefix: HEADING_PREFIX.DECIMAL.id,
  numbering_format_readable: '{i}. {header}',
};

const SectionForm = ({ initials, dataItems, isResetForm, onSubmit }) => {
  const [form] = Form.useForm();
  const [isNumbering, setIsNumbering] = useState(false);
  const [sectionType, setSectionType] = useState(
    YELLOW_BOOKS_SECTION_TYPES.TEXT.id,
  );
  const [dataItemType, setDataItemType] = useState(null);
  const { t } = useTranslation();

  const dataItemsOptions = dataItems?.map((item) => ({
    label: item.name,
    value: item.id,
  }));

  const relevantDataItem = dataItems?.find((item) => item.id === dataItemType);

  useEffect(() => {
    if (!initials) return;

    if (initials.item_type) setSectionType(initials.item_type);
    if (initials.is_numbering) setIsNumbering(initials.is_numbering);
    if (initials.yb_data_item_id) setDataItemType(initials.yb_data_item_id);
  }, [initials]);

  useEffect(() => {
    if (isResetForm) {
      form.resetFields();

      setSectionType(YELLOW_BOOKS_SECTION_TYPES.TEXT.id);
      setIsNumbering(false);
      setDataItemType(null);
    }
  }, [form, isResetForm]);

  const renderTextType = () => (
    <>
      <FormTextEditor label={t('Obsah')} name={'content_sk'} />
    </>
  );

  const renderParam = (param) => {
    if (param.type === 'option' && param.optionList?.length > 0) {
      return (
        <Col span={8} key={param.id}>
          <FormSelect
            label={param.name}
            name={['data_item_params', param.order - 1]}
            options={param.optionList.map((option) => ({
              label: option.label,
              value: option.value,
            }))}
            rules={[required(t)]}
          />
        </Col>
      );
    }

    return (
      <Col span={8} key={param.id}>
        <FormTextField
          label={param.name}
          name={['data_item_params', param.order - 1]}
          options={param.optionsList}
          value={'25'}
          rules={[required(t)]}
        />
      </Col>
    );
  };

  const renderDataType = () => (
    <>
      <FormTextEditor
        label={t('Text pred blokom')}
        name={'content_before'}
        height={200}
      />
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <FormSelect
            label={t('Dátový modul')}
            name="yb_data_item_id"
            options={dataItemsOptions}
            rules={[required(t)]}
            onChange={setDataItemType}
            placeholder={t('Vyberte dátový modul')}
          />
        </Col>
        {dataItemType && relevantDataItem?.params?.map(renderParam)}
      </Row>
      {dataItemType && relevantDataItem && (
        <Form.Item label={t('Popis modulu')}>
          <p>{relevantDataItem.description}</p>
        </Form.Item>
      )}
      <FormTextEditor
        label={t('Text za blokom')}
        name={'content_after'}
        height={200}
      />
    </>
  );

  const renderRelevantForm = () => {
    switch (sectionType) {
      case YELLOW_BOOKS_SECTION_TYPES.TEXT.id:
        return renderTextType();
      case YELLOW_BOOKS_SECTION_TYPES.HEADING.id:
        return null;
      case YELLOW_BOOKS_SECTION_TYPES.DATA.id:
        return renderDataType();
      default:
        return null;
    }
  };

  const tooltipText = (
    <div>
      <p>{t('Vlastné formátovanie nadpisov.')}</p>
      <p>
        {'{i}'} {t('supluje zvolené číslovanie')}
        <br />
        {'{header}'} {t('supluje text nadpisu')}
        <br />
        \n {t('text na novom riadku')}
      </p>
    </div>
  );

  return (
    <Form
      onFinish={onSubmit}
      initialValues={initials ?? defaultValues}
      form={form}
    >
      <CollapseBlock title={t('Typ sekcie')} collapsible>
        <FormRadioGroup
          name={'item_type'}
          options={YELLOW_BOOKS_SECTION_TYPES.list.map((option) => ({
            ...option,
            label: t(option.label),
          }))}
          onChange={setSectionType}
          value={sectionType}
        />
      </CollapseBlock>
      <CollapseBlock title={t('Nastavenia pre vybraný typ sekcie')} collapsible>
        <FormTextField
          label={t('Názov')}
          name={'title_sk'}
          rules={[required(t)]}
        />
        {renderRelevantForm()}
      </CollapseBlock>
      <CollapseBlock title={t('Spoločné nastavenia')} collapsible>
        {!!initials?.parent_id && (
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <FormSwitch
                label={t('Zalomiť stranu pred blokom')}
                name={'is_pagebreak_before'}
              />
            </Col>
            <Col span={6}>
              <FormSwitch
                label={t('Nadpis bude odkaz')}
                name={'is_heading_linkable'}
              />
            </Col>
          </Row>
        )}
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <FormSwitch
              label={t('Číslovať vnorené nadpisy')}
              name={'is_numbering'}
              onChange={setIsNumbering}
            />
          </Col>
          {isNumbering && (
            <>
              <Col span={6}>
                <FormRadioGroup
                  label={t('Centrovanie')}
                  name={'numbering_align'}
                  options={TEXT_ALIGN.list.map((option) => ({
                    ...option,
                    label: t(option.label),
                  }))}
                  optionType={'button'}
                />
              </Col>
              <Col span={5}>
                <FormRadioGroup
                  label={t('Číslovanie')}
                  name={'numbering_prefix'}
                  options={HEADING_PREFIX.list}
                  optionType={'button'}
                />
              </Col>
              <Col span={6}>
                <FormTextField
                  label={t('Formát')}
                  name={'numbering_format_readable'}
                />
              </Col>
              <Col span={1}>
                <Tooltip title={tooltipText} placement="topLeft">
                  <InfoCircleOutlined />
                </Tooltip>
              </Col>
            </>
          )}
          {/* <Col span={10}> // TODO: uncomment when EN is needed
            <FormTextField
              label={t('Formát číslovania v anglickej verzii')}
              name={'numbering_format_en'}
            />
          </Col> */}
        </Row>
      </CollapseBlock>
      <FormButtonsContainer>
        <FormButton submitOnClick={true} submitKey={'yellowBooks'}>
          {t('Uložiť')}
        </FormButton>
      </FormButtonsContainer>
    </Form>
  );
};

export default SectionForm;
